import { useEffect, useState } from "react";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

const monthly = [
  { key: "basic_monthly", name: "Basic", price: "4", period: "mese", features: ["1 menù digitale", "Categorie e piatti illimitati", "QR Code + link condivisibile", "Foto dei piatti", "Allergeni per ogni piatto", "FoodTruck: luogo e orario nel menù", "Assistenza via email"] },
  { key: "avanzato_monthly", name: "Avanzato", price: "16", period: "mese", features: ["Tutto in Basic", "Logo e copertina del locale", "Contatti e prenotazioni", "Menù del giorno", "Programma FoodTruck settimanale", "AI: suggerimento allergeni", "Assistenza prioritaria"] },
  { key: "pro_monthly", name: "Pro", price: "29", period: "mese", highlight: true, features: ["Tutto in Avanzato", "Pagamento del conto al tavolo", "Prodotto personalizzato ordinabile dal cliente", "Più design di menù tra cui scegliere", "Colore dei pulsanti personalizzato", "Fino a 2 menù", "Traduzione in inglese"] },
  { key: "business_monthly", name: "Business", price: "49", period: "mese", features: ["Tutto in Pro", "Font personalizzato per i titoli", "Lista d'attesa clienti", "Prenotazioni da asporto con orario", "Filtro allergeni + etichette Vegetariano/Vegano/Senza glutine", "Prezzi multipli per lo stesso prodotto", "Notifiche push e WhatsApp (ordini e prenotazioni)", "Menù illimitati", "Multilingua (inglese + extra)"] },
];
const daily = [
  { key: "tempo1_daily", name: "A Tempo", price: "1,99" },
];

export default function Billing() {
  const { user, checkAuth } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => { checkAuth(); }, []); // eslint-disable-line

  const buy = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div>
        <h1 className="font-display font-bold text-4xl">Abbonamento</h1>
        <p className="text-gray-500 mt-1">Scegli il piano più adatto alla tua attività. <span className="text-[#5aa469] font-medium">14 giorni di prova gratuita</span>, poi decidi se restare sul piano Free o continuare con quello a pagamento. <span className="text-gray-400">IVA inclusa.</span></p>
      </div>

      {user && !user.is_paid && (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 flex items-center gap-3 flex-wrap" data-testid="free-plan-banner">
          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div>
          <div>
            <p className="font-semibold">Piano attivo: Free</p>
            <p className="text-sm text-gray-500">Categorie, piatti, allergeni e QR Code, con il logo MenuOne sul menù. Passa a Basic per foto, AI e contatti del locale.</p>
          </div>
        </div>
      )}

      {user?.subscription?.status === "active" && (
        <div className="rounded-2xl border border-[#5aa469]/40 bg-[#5aa469]/8 p-5 flex items-center gap-3 flex-wrap" data-testid="active-sub-banner">
          <div className="h-10 w-10 rounded-full bg-[#5aa469] flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div>
          <div>
            <p className="font-semibold">Piano attivo: {user.subscription.name}</p>
            <p className="text-sm text-gray-500">
              {user.subscription.started_at && <>Attivato il {fmtDate(user.subscription.started_at)}</>}
              {user.subscription.renews_at && <> · <span className="text-[#4a8a56] font-medium" data-testid="billing-renews">{user.subscription.lookup_key?.endsWith("_daily") ? "Scade il" : "Prossimo rinnovo"} {fmtDate(user.subscription.renews_at)}</span></>}
            </p>
          </div>
          <Button onClick={openPortal} disabled={portalLoading} variant="outline" className="sm:ml-auto rounded-full border-[#5aa469]/50 bg-white text-[#4a8a56] hover:bg-[#5aa469]/10 font-semibold" data-testid="manage-subscription-btn">
            {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gestisci abbonamento"}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
        {monthly.map((p) => {
          const active = user?.subscription?.lookup_key === p.key;
          const hasOther = user?.subscription?.status === "active" && !active;
          return (
            <div key={p.key} className={`rounded-3xl p-7 flex flex-col transition-[transform,border-color] hover:-translate-y-1 ${p.highlight ? "bg-white border border-[#5aa469]/60 mo-glow" : "mo-card hover:border-gray-300"}`} data-testid={`billing-plan-${p.key}`}>
              {p.highlight && <span className="self-start mb-3 text-xs font-semibold uppercase text-white bg-[#5aa469] px-3 py-1 rounded-full">Consigliato</span>}
              <h3 className="font-display font-bold text-3xl">{p.name}</h3>
              <div className="mt-2 mb-1.5 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold font-display">{p.price} €</span><span className="text-gray-500">/{p.period}</span>
              </div>
              <p className="mb-5 text-sm font-medium text-[#4a8a56]" data-testid={`billing-trial-${p.key}`}>Prova gratis 14 giorni, poi {p.price} €/mese</p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-gray-700"><Check className="h-4 w-4 text-[#5aa469] mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              <Button onClick={() => buy(p.key)} disabled={loadingKey === p.key || active} className={`rounded-full w-full h-11 active:scale-95 transition-transform ${p.highlight ? "bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" : "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50"}`} data-testid={`buy-${p.key}`}>
                {active ? "Piano attuale" : loadingKey === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : hasOther ? "Passa a questo" : "Inizia prova gratis"}
              </Button>
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="font-display font-semibold text-2xl mb-4">Piani a tempo (24h)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {daily.map((p) => {
            const active = user?.subscription?.lookup_key === p.key;
            return (
              <div key={p.key} className="rounded-2xl mo-card p-5 flex items-center justify-between" data-testid={`billing-plan-${p.key}`}>
                <div>
                  <p className="font-display font-semibold text-xl">{p.name}</p>
                  <p className="text-2xl font-extrabold font-display">{p.price} €<span className="text-sm font-normal text-gray-500">/giorno</span></p>
                </div>
                <Button onClick={() => buy(p.key)} disabled={loadingKey === p.key || active} variant="outline" className="rounded-full border-gray-300 bg-white text-gray-900 hover:bg-gray-50" data-testid={`buy-${p.key}`}>
                  {active ? "Attivo" : loadingKey === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Attiva"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-gray-400">Pagamenti sicuri gestiti da Stripe. Usa la carta di test 4242 4242 4242 4242 con una data futura e un CVC qualsiasi.</p>
    </div>
  );
}
